import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { apiMessage, apiStatus, isSignedIn } from "./fanHomeApi";
import { discoveryApi } from "./discoveryApi";
import { usePlayer } from "./PlayerContext";
import MusicShell from "./MusicShell";
import { Notice } from "./discoveryParts";
import { ConfirmDialog, CoverArt, INK, MUTED, ListPanel, ListSkeleton, OutlineButton, PlaylistNameDialog, SoundRow, SolidButton, useToast } from "./libraryParts";

// One playlist, laid out like the playlist screen in the Claude design: cover, name and count, then the sounds in order, each
// with Play on its square. Play all plays the list from the top. My own playlist can also be renamed or deleted (after a
// confirmation) and a sound can be removed from it. Sounds that were removed from the platform are left out and counted.
export default function PlaylistPage() {
  const { id } = useParams();
  const { play } = usePlayer();
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, notify] = useToast();
  // Opened from Search or the library: go back there. Opened from a shared link: go to Search (guests have no library).
  const cameFromApp = location.key !== "default";
  const goBack = () => (cameFromApp ? navigate(-1) : navigate("/fan/search"));
  const [state, setState] = useState({ status: "loading", data: null, error: "", missing: false });
  const [dialog, setDialog] = useState(null); // "rename" | "delete" | null
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading", error: "" }));
    try {
      setState({ status: "ready", data: await discoveryApi.playlist(id), error: "", missing: false });
    } catch (e) {
      setState({ status: "error", data: null, error: apiMessage(e, "We couldn't load this playlist."), missing: apiStatus(e) === 404 });
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const data = state.data;

  const rename = async (name) => {
    const saved = await discoveryApi.renamePlaylist(data.id, name);
    setState((s) => ({ ...s, data: { ...s.data, name: saved.name } }));
    setDialog(null);
    notify("Playlist renamed.");
  };
  const remove = async () => {
    await discoveryApi.deletePlaylist(data.id);
    navigate("/fan/library?tab=playlists", { replace: true, state: { notice: `Playlist “${data.name}” was deleted.` } });
  };
  // The sound leaves the list at once; if the server refuses it comes back.
  const removeSound = async (track) => {
    if (removing) return;
    const before = state.data;
    setRemoving(track.id);
    setState((s) => ({ ...s, data: { ...s.data, items: s.data.items.filter((t) => t.id !== track.id) } }));
    try {
      await discoveryApi.removePlaylistTrack(data.id, track.id);
      notify(`“${track.title}” was removed from the playlist.`);
    } catch (e) {
      setState((s) => ({ ...s, data: before }));
      notify(apiMessage(e, "We couldn't remove this sound. Please try again."));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <MusicShell>
      <button type="button" onClick={goBack} className="text-[13px] font-semibold hover:underline" style={{ color: MUTED }}>← {isSignedIn() ? "Back to Playlists" : "Back to search"}</button>
      {state.status === "loading" ? (
        <div className="mt-6"><ListSkeleton /></div>
      ) : state.status === "error" ? (
        <div className="mt-6">
          {state.missing
            ? <Notice action={<Link to={isSignedIn() ? "/fan/library?tab=playlists" : "/fan/search"} className="mt-3 inline-block rounded-full px-[18px] py-2 text-[13px] font-semibold text-white" style={{ backgroundColor: ACCENT }}>{isSignedIn() ? "Back to My Library" : "Back to search"}</Link>}>This playlist could not be found. It may have been deleted or made private.</Notice>
            : <Notice action={<SolidButton onClick={load} className="mt-3">Retry</SolidButton>}><span role="alert">{state.error}</span></Notice>}
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-end gap-x-5 gap-y-4">
            <CoverArt url={data.cover_url} seed={data.id} className="h-[108px] w-[108px] shrink-0 rounded-2xl" />
            <div className="min-w-0 flex-1 basis-56">
              <h1 className="truncate text-[26px] font-bold leading-tight" style={{ color: INK }} data-testid="playlist-name" title={data.name}>{data.name}</h1>
              <p className="mt-1 text-[13px]" style={{ color: MUTED }}>{data.items.length} {data.items.length === 1 ? "sound" : "sounds"}{data.owner_name && !data.mine ? ` · by ${data.owner_name}` : ""}</p>
              <SolidButton onClick={() => play(data.items[0], { queue: data.items })} disabled={!data.items.length} className="mt-3" data-testid="play-all">▶ Play all</SolidButton>
            </div>
            {data.mine && (
              <div className="flex gap-3">
                <OutlineButton onClick={() => setDialog("rename")} data-testid="rename-playlist">Rename</OutlineButton>
                <OutlineButton danger onClick={() => setDialog("delete")} data-testid="delete-playlist">Delete Playlist</OutlineButton>
              </div>
            )}
          </div>
          {data.unavailable_count > 0 && <p className="mt-4 text-sm" style={{ color: MUTED }}>{data.unavailable_count} {data.unavailable_count === 1 ? "sound is" : "sounds are"} no longer available and {data.unavailable_count === 1 ? "is" : "are"} hidden.</p>}
          <div className="mt-6">
            {data.items.length
              ? (
                <ListPanel>
                  <ol data-testid="playlist-sounds">
                    {data.items.map((t, i, all) => (
                      <SoundRow
                        key={t.id} track={t} queue={all}
                        action={data.mine && <button type="button" onClick={() => removeSound(t)} disabled={removing === t.id} aria-label={`Remove ${t.title} from this playlist`} className="shrink-0 text-[13px] font-semibold hover:text-red-700 disabled:opacity-60" style={{ color: MUTED }}>Remove</button>}
                      />
                    ))}
                  </ol>
                </ListPanel>
              )
              : <Notice>{data.mine ? <>This playlist has no sounds yet. Use “Add to playlist” on a sound page. <Link to="/fan/search" className="font-semibold underline" style={{ color: ACCENT }}>Find sounds</Link></> : "This playlist has no playable sounds yet."}</Notice>}
          </div>
        </>
      )}
      {dialog === "rename" && data && <PlaylistNameDialog title="Rename playlist" confirmLabel="Save" initial={data.name} onSubmit={rename} onClose={() => setDialog(null)} />}
      {dialog === "delete" && data && (
        <ConfirmDialog
          title={`Delete “${data.name}”?`} confirmLabel="Delete Playlist" cancelLabel="Keep Playlist" onConfirm={remove} onClose={() => setDialog(null)}
          message="This removes the playlist. The sounds themselves stay saved wherever else you've added them."
        />
      )}
      {toast}
    </MusicShell>
  );
}
